/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

/**
 * Current open issues:
 * How do  we decide if the role is editable and how do we make the
 * UI respond appropriately (i.e. display the dropdown or the grayed
 * out thing)?
 *
 * Inputs:
 *   list of roles
 *   currently selected role
 * Outputs:
 *   what happens on selection change...?
 */
import {
	Component,
	Input,
	ChangeDetectionStrategy,
	Self,
	Optional,
	ChangeDetectorRef,
	OnInit,
	OnDestroy,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Role, ProjectMember } from '@touchplan/common';
import { Subject, Observable } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import * as strings from '@touchplan/strings';

/**
 * Displays the ticket's role.  If the role is editable, then it's
 * displayed as a dropdown.  If it's not editable, it's displayed
 * in a grayed out panel.
 *
 * More detail...
 */
@Component({
	selector: 'ticket-role',
	templateUrl: './ticket-role.component.html',
	host: { class: 'ticket-role--style' },
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketRoleComponent extends TicketEditBaseComponent implements OnInit, OnDestroy {
	/**
	 * @input roles - The list of roles to choose from
	 */
	@Input() public roleList: Subject<Map<string, Role>>;
	@Input() public projectMember: ProjectMember;
	@Input() public allowNullSelection = false;
	@Input() public placeholder = strings.TICKET_PLACEHOLDER_ROLE;

	public roleList$: Observable<Map<string, Role>>;
	public notSetLabel = strings.GENERAL_NONE;

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef
	) {
		super(control, multiWrapper, cd);
	}

	public ngOnInit() {
		super.ngOnInit();
		this.internalField.valueChanges.pipe(takeUntil(this.shutdownSubject)).subscribe((val: number) => {
			this.onChange(val);
		});

		this.roleList$ = this.roleList.pipe(
			takeUntil(this.shutdownSubject),
			map((list: Map<string, Role>) => {
				const result = new Map<string, Role>();
				list.forEach((role: Role, roleId: string) => {
					if (
						(!this.projectMember?.view.secondaryRoles || !this.projectMember?.view.secondaryRoles[roleId]) &&
						// Checks if the role is the active one on the ticket. This is needed to show the role in tickets that user don't have access to
						this.internalField.value !== roleId &&
						this.projectMember.view.accessLevel === 'General'
					) {
						return;
					}

					result.set(roleId, role);
				});
				return result;
			})
		);
	}
}
